import React, { useState } from 'react';
import { parseUserAgent, sessionDurationMinutes } from '../utils/parseUserAgent';

function OsIcon({ os }) {
  const key = String(os || '').toLowerCase();
  if (key.includes('windows'))
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-sky-100 text-sky-800" aria-hidden>
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 5.5l7.5-1v7.5H3V5.5zm0 8.5h7.5V22L3 20.5V14zm9-9.5L21 3v10h-9V4.5zm0 11.5h9V21l-9-1.5V16z" />
        </svg>
      </span>
    );
  if (key.includes('android'))
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-green-100 text-green-800 text-lg" aria-hidden>
        🤖
      </span>
    );
  if (key.includes('mac') || key.includes('ios'))
    return (
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-800" aria-hidden>
        
      </span>
    );
  return (
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-600" aria-hidden>
      ◻
    </span>
  );
}

const btnBerry =
  'rounded-md px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50 bg-[#6B1520] hover:bg-[#551018]';

/**
 * Liste des sessions (login audit) — style tableau / carte expansible.
 */
export default function SessionHistoryList({
  sessions = [],
  totalSessions = 0,
  formatDateTime,
  loading,
}) {
  const [expanded, setExpanded] = useState(() => ({}));

  const toggle = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600 py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-entrea-royal" />
        Chargement des sessions…
      </div>
    );
  }

  if (!sessions.length) {
    return <p className="text-sm text-gray-500 py-4">Aucune connexion enregistrée.</p>;
  }

  return (
    <div className="space-y-4">
      {totalSessions > 0 ? (
        <p className="text-xs text-gray-500">
          {totalSessions} connexion{totalSessions > 1 ? 's' : ''} enregistrée{totalSessions > 1 ? 's' : ''}
          {sessions.length < totalSessions ? ` (affichage des ${sessions.length} plus récentes)` : ''}
        </p>
      ) : null}

      {/* En-têtes type tableau (desktop) */}
      <div className="hidden md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] md:gap-3 md:items-center md:px-3 md:pb-2 text-xs font-bold uppercase tracking-wide text-gray-600 border-b border-gray-200">
        <span>Système</span>
        <span>Navigateur</span>
        <span>Localisation</span>
        <span>Adresse IP</span>
        <span className="text-center">Action</span>
        <span className="sr-only">Détails</span>
      </div>

      <ul className="space-y-3">
        {sessions.map((session, index) => {
          const ua = parseUserAgent(session.user_agent);
          const key = `${index}-${session.login_time}`;
          const isOpen = expanded[key];
          const dur = sessionDurationMinutes(session, index, sessions);
          const durLabel =
            dur != null ? `${dur.toFixed(dur >= 10 ? 1 : 2)} min` : '—';

          return (
            <li
              key={key}
              className="rounded-lg border border-gray-200 bg-gray-50/80 shadow-sm overflow-hidden"
            >
              <div className="flex flex-col gap-3 p-3 md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] md:gap-3 md:items-center">
                <div className="flex items-center gap-2 min-w-0">
                  <OsIcon os={ua.os} />
                  <span className="text-sm font-medium text-gray-900 truncate">{ua.os}</span>
                  {session.is_active ? (
                    <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800">
                      Actif
                    </span>
                  ) : null}
                </div>
                <div className="text-sm text-gray-800 font-medium">{ua.browser}</div>
                <div className="text-sm text-gray-600">
                  <span className="md:hidden font-semibold text-gray-500 text-xs uppercase mr-2">Lieu</span>
                  <span>Non localisé</span>
                  <span className="block text-[11px] text-gray-400 normal-case mt-0.5 md:inline md:ml-1 md:mt-0">
                    (géolocalisation IP non activée)
                  </span>
                </div>
                <div className="text-sm font-mono text-gray-900">{session.ip_address || '—'}</div>
                <div className="flex md:justify-center">
                  <button type="button" className={btnBerry} disabled title="Bientôt disponible">
                    Geler
                  </button>
                </div>
                <div className="flex justify-end md:justify-center">
                  <button
                    type="button"
                    onClick={() => toggle(key)}
                    className="rounded-md p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? 'Réduire le détail' : 'Afficher le détail'}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                      {isOpen ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                        />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 bg-white px-3 py-2 text-xs text-gray-500 md:hidden">
                Connexion :{' '}
                <span className="font-medium text-gray-800">
                  {session.login_time && formatDateTime
                    ? formatDateTime(
                        typeof session.login_time === 'string'
                          ? session.login_time
                          : new Date(session.login_time).toISOString()
                      )
                    : '—'}
                </span>
              </div>

              {isOpen ? (
                <div className="border-t border-gray-200 bg-white px-4 py-4">
                  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase text-gray-500">Appareil</p>
                        <p className="mt-1 font-medium text-gray-900">{ua.device}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase text-gray-500">Durée de session</p>
                        <p className="mt-1 font-mono font-medium text-gray-900">{durLabel}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <p className="text-xs font-semibold uppercase text-gray-500">Connexion</p>
                        <p className="mt-1 text-gray-800">
                          {session.login_time && formatDateTime
                            ? formatDateTime(
                                typeof session.login_time === 'string'
                                  ? session.login_time
                                  : new Date(session.login_time).toISOString()
                              )
                            : '—'}
                        </p>
                      </div>
                      {session.user_agent ? (
                        <div className="sm:col-span-2">
                          <p className="text-xs font-semibold uppercase text-gray-500">User-Agent</p>
                          <p className="mt-1 break-all text-gray-600">{String(session.user_agent)}</p>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex justify-end shrink-0">
                      <button type="button" className={`${btnBerry} px-4 py-2 text-sm`} disabled title="Bientôt disponible">
                        Terminer la session
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
